import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';
const LOGO_DARK_URL = 'https://customer-assets.emergentagent.com/job_f9f64a4c-3451-4815-8703-6feb7c894858/artifacts/mema1897_Updated%20Logo.png';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme?.() || {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="privacy-policy-page">
      <header className="bg-slate-900 dark:bg-slate-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Helios" className="h-8" />
          <Button variant="ghost" size="sm" className="text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed text-slate-700 dark:text-slate-300">

          <Section title="1. Who We Are">
            <p>Helios Electrical & Solar ("we", "us", "our") is a solar installation company registered in England. Our registered address is 51 Burners Lane South, Kiln Farm, Milton Keynes, MK11 3HA.</p>
            <p>Our Data Protection Officer can be contacted at: <a href="mailto:nick@helioses.co.uk" className="text-blue-600 dark:text-blue-400 underline">nick@helioses.co.uk</a></p>
          </Section>

          <Section title="2. What Data We Collect">
            <p>We collect and process the following personal data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Identity data:</strong> First name, last name, title</li>
              <li><strong>Contact data:</strong> Email address, telephone numbers, postal address</li>
              <li><strong>Technical data:</strong> IP address, browser type, device information when using our portal</li>
              <li><strong>Installation data:</strong> Property details, energy usage, system specifications, installation photos</li>
              <li><strong>Financial data:</strong> Payment information (processed securely via Stripe), invoices, quotes</li>
              <li><strong>Communication data:</strong> Messages sent through our customer portal, emails, support tickets</li>
              <li><strong>Marketing data:</strong> Lead information from Meta (Facebook) Lead Ads, including form submissions</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide and manage your solar installation service</li>
              <li>To communicate with you about your project via our customer portal</li>
              <li>To process payments and manage invoicing</li>
              <li>To schedule engineer visits and manage installations</li>
              <li>To monitor your solar system performance (with your consent)</li>
              <li>To send project updates and status notifications</li>
              <li>To comply with legal and regulatory obligations</li>
            </ul>
          </Section>

          <Section title="4. Legal Basis for Processing">
            <p>We process your data under the following legal bases (UK GDPR Article 6):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contract performance:</strong> Processing necessary to fulfil our installation contract with you</li>
              <li><strong>Legitimate interests:</strong> Business administration, service improvement, fraud prevention</li>
              <li><strong>Legal obligation:</strong> Tax records, regulatory compliance, health & safety</li>
              <li><strong>Consent:</strong> Marketing communications, solar monitoring data, cookies</li>
            </ul>
          </Section>

          <Section title="5. Data Sharing">
            <p>We may share your data with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Payment processors:</strong> Stripe, for secure payment processing</li>
              <li><strong>Accounting software:</strong> Xero, for invoicing and financial records</li>
              <li><strong>Email services:</strong> Microsoft 365, for communication</li>
              <li><strong>Solar monitoring platforms:</strong> Fox ESS Cloud, Growatt (for system performance data)</li>
              <li><strong>Regulatory bodies:</strong> HIES, MCS, Ofgem as required for certification</li>
            </ul>
            <p>We do not sell your personal data to third parties.</p>
          </Section>

          <Section title="6. Data Retention">
            <p>We retain your data for the following periods:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Customer records:</strong> Duration of our business relationship plus 6 years (UK tax law)</li>
              <li><strong>Financial records:</strong> 6 years from the end of the financial year (HMRC requirement)</li>
              <li><strong>Installation records:</strong> 25 years (warranty and certification compliance)</li>
              <li><strong>Marketing leads:</strong> 12 months if not converted to a customer</li>
              <li><strong>Communication logs:</strong> 3 years from last interaction</li>
            </ul>
          </Section>

          <Section title="7. Your Rights">
            <p>Under UK GDPR, you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Right of access:</strong> Request a copy of all data we hold about you</li>
              <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Right to data portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Right to restrict processing:</strong> Request we limit how we use your data</li>
              <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p>To exercise any of these rights, please contact us at <a href="mailto:nick@helioses.co.uk" className="text-blue-600 dark:text-blue-400 underline">nick@helioses.co.uk</a> or use the self-service options in your customer portal.</p>
            <p>We will respond to all requests within 30 days.</p>
          </Section>

          <Section title="8. Cookies & Local Storage">
            <p>Our portal uses:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Essential cookies/storage:</strong> Authentication tokens (required for portal access)</li>
              <li><strong>Preference storage:</strong> Theme preference (dark/light mode), view preferences</li>
            </ul>
            <p>We do not use third-party tracking cookies or analytics cookies.</p>
          </Section>

          <Section title="9. Data Security">
            <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Password hashing using industry-standard algorithms</li>
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Role-based access controls</li>
              <li>Audit logging of data access</li>
              <li>Secure payment processing via PCI-compliant providers</li>
            </ul>
          </Section>

          <Section title="10. Complaints">
            <p>If you are unhappy with how we handle your data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">ico.org.uk</a></li>
              <li>Telephone: 0303 123 1113</li>
            </ul>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this privacy policy from time to time. We will notify you of significant changes via email or through our customer portal.</p>
          </Section>

        </div>
      </main>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h2>
    {children}
  </section>
);

export default PrivacyPolicy;
